export interface RoleAssignment {
  roleId: number;
  accountId?: number | null;
  assigneeId?: number | null;
}

export interface RoleAssignmentRole {
  id: number;
  name: string;
  projects: boolean;
  items: boolean;
}

export interface RoleAssignmentOption {
  key: string;
  label: string;
  shortLabel: string;
  accountId?: number;
  assigneeId?: number;
}

export type RoleAssignmentOptionsByRoleId = Record<number, RoleAssignmentOption[]>;

export interface RoleAssignmentManageTeamMember {
  accountId?: number | null;
  userId?: string | number | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  label?: string | null;
  roles?: number[] | null;
}

export interface RoleAssignmentManageAssignee {
  id?: number | null;
  name?: string | null;
  label?: string | null;
  roles?: number[] | null;
}

export interface RoleAssignmentManageEntry {
  key: string;
  type: 'team_member' | 'assignee';
  name: string;
  label: string;
  roleIds: number[];
  teamMember?: RoleAssignmentManageTeamMember;
  assignee?: RoleAssignmentManageAssignee;
}

interface RoleAssignableTeamMember {
  accountId: number;
  name: string;
  label: string;
  roleIds: number[];
}

interface RoleAssignableAssignee {
  assigneeId: number;
  name: string;
  label: string;
  roleIds: number[];
}

const normalizeRoleIds = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return [
    ...new Set(
      value
        .map((roleId) => Number(roleId))
        .filter((roleId) => Number.isInteger(roleId) && roleId > 0)
    ),
  ];
};

const normalizeLabel = (value: unknown): string => {
  return String(value || '')
    .replace(/^@+/, '')
    .trim()
    .toUpperCase();
};

export const normalizeRoleAssignments = (value: unknown): RoleAssignment[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const result: RoleAssignment[] = [];
  const seenRoleIds = new Set<number>();

  for (const entry of value) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }

    const roleId = Number((entry as any).roleId);
    if (!Number.isInteger(roleId) || roleId <= 0 || seenRoleIds.has(roleId)) {
      continue;
    }

    const accountIdRaw = Number((entry as any).accountId);
    const assigneeIdRaw = Number((entry as any).assigneeId);
    const accountId = Number.isInteger(accountIdRaw) && accountIdRaw > 0 ? accountIdRaw : null;
    const assigneeId = Number.isInteger(assigneeIdRaw) && assigneeIdRaw > 0 ? assigneeIdRaw : null;

    if (!accountId && !assigneeId) {
      continue;
    }

    result.push({
      roleId,
      accountId,
      assigneeId,
    });
    seenRoleIds.add(roleId);
  }

  return result;
};

export const resolveRoleAssignmentOwnerAccountId = (
  accountId: number,
  teamContext: any
): number => {
  const ownerAccountId = Number(teamContext?.ownerAccountId);
  if (Number.isInteger(ownerAccountId) && ownerAccountId > 0) {
    return ownerAccountId;
  }
  const ownerAccountFromData = Number(teamContext?.owner?.accountId);
  if (Number.isInteger(ownerAccountFromData) && ownerAccountFromData > 0) {
    return ownerAccountFromData;
  }
  const localOwnerAccountId =
    typeof window !== 'undefined' ? Number(localStorage.getItem('ownerAccountId') || '0') : 0;
  if (Number.isInteger(localOwnerAccountId) && localOwnerAccountId > 0) {
    return localOwnerAccountId;
  }
  return accountId;
};

export const getRoleColumnsForTarget = (
  roles: unknown,
  target: 'projects' | 'items'
): RoleAssignmentRole[] => {
  if (!Array.isArray(roles)) {
    return [];
  }

  return roles
    .map((role) => {
      if (!role || typeof role !== 'object') {
        return null;
      }
      const id = Number((role as any).id);
      if (!Number.isInteger(id) || id <= 0) {
        return null;
      }
      const normalizedRole: RoleAssignmentRole = {
        id,
        name: String((role as any).name || '').trim(),
        projects: !!(role as any).projects,
        items: !!(role as any).items,
      };
      if (!normalizedRole.name) {
        return null;
      }
      return normalizedRole;
    })
    .filter((role): role is RoleAssignmentRole => !!role)
    .filter((role) => (target === 'projects' ? role.projects : role.items));
};

export const extractTeamMembersForRoleAssignments = (
  teamContext: any
): RoleAssignableTeamMember[] => {
  const teamMembers = Array.isArray(teamContext?.teamMembers) ? teamContext.teamMembers : [];

  return teamMembers
    .map((teamMember) => {
      const accountId = Number(teamMember?.accountId);
      if (!Number.isInteger(accountId) || accountId <= 0) {
        return null;
      }
      const firstName = String(teamMember?.firstName || '').trim();
      const lastName = String(teamMember?.lastName || '').trim();
      const email = String(teamMember?.email || '').trim();
      const fullName = `${firstName} ${lastName}`.trim();
      const fallbackName = fullName || email || 'Team Member';
      const label = normalizeLabel(teamMember?.label);

      return {
        accountId,
        name: fallbackName,
        label,
        roleIds: normalizeRoleIds(teamMember?.roles),
      };
    })
    .filter((teamMember): teamMember is RoleAssignableTeamMember => !!teamMember);
};

export const extractAssigneesForRoleAssignments = (
  assignees: unknown
): RoleAssignableAssignee[] => {
  if (!Array.isArray(assignees)) {
    return [];
  }

  return assignees
    .map((assignee) => {
      const assigneeId = Number((assignee as any)?.id);
      if (!Number.isInteger(assigneeId) || assigneeId <= 0) {
        return null;
      }
      const name = String((assignee as any)?.name || '').trim();
      if (!name) {
        return null;
      }
      return {
        assigneeId,
        name,
        label: normalizeLabel((assignee as any)?.label),
        roleIds: normalizeRoleIds((assignee as any)?.roles),
      };
    })
    .filter((assignee): assignee is RoleAssignableAssignee => !!assignee);
};

const buildMemberOptionLabel = (
  name: string,
  label: string
): { label: string; shortLabel: string } => {
  const normalizedLabel = normalizeLabel(label);
  if (!normalizedLabel) {
    return {
      label: name,
      shortLabel: name,
    };
  }
  return {
    label: `${name} @${normalizedLabel}`,
    shortLabel: normalizedLabel,
  };
};

const buildAssigneeOptionLabel = (
  name: string,
  label: string
): { label: string; shortLabel: string } => {
  const normalizedLabel = normalizeLabel(label);
  if (!normalizedLabel) {
    return {
      label: name,
      shortLabel: name,
    };
  }
  return {
    label: `${name} @${normalizedLabel}`,
    shortLabel: normalizedLabel,
  };
};

export const buildRoleAssignmentOptionsByRoleId = (
  roles: RoleAssignmentRole[],
  teamMembers: RoleAssignableTeamMember[],
  assignees: RoleAssignableAssignee[]
): RoleAssignmentOptionsByRoleId => {
  const optionsByRoleId: RoleAssignmentOptionsByRoleId = {};

  for (const role of roles) {
    const options: RoleAssignmentOption[] = [];
    const usedKeys = new Set<string>();

    for (const teamMember of teamMembers) {
      if (!teamMember.roleIds.includes(role.id)) {
        continue;
      }

      const key = `account:${teamMember.accountId}`;
      if (usedKeys.has(key)) {
        continue;
      }
      const optionLabels = buildMemberOptionLabel(teamMember.name, teamMember.label);

      options.push({
        key,
        label: optionLabels.label,
        shortLabel: optionLabels.shortLabel,
        accountId: teamMember.accountId,
      });
      usedKeys.add(key);
    }

    for (const assignee of assignees) {
      if (!assignee.roleIds.includes(role.id)) {
        continue;
      }

      const key = `assignee:${assignee.assigneeId}`;
      if (usedKeys.has(key)) {
        continue;
      }
      const optionLabels = buildAssigneeOptionLabel(assignee.name, assignee.label);

      options.push({
        key,
        label: optionLabels.label,
        shortLabel: optionLabels.shortLabel,
        assigneeId: assignee.assigneeId,
      });
      usedKeys.add(key);
    }

    optionsByRoleId[role.id] = options;
  }

  return optionsByRoleId;
};

export const getRoleAssignmentSelectedOptionKey = (
  assignments: unknown,
  roleId: number
): string => {
  const normalizedAssignments = normalizeRoleAssignments(assignments);
  const found = normalizedAssignments.find((assignment) => assignment.roleId === roleId);
  if (!found) {
    return '';
  }
  if (Number.isInteger(found.accountId) && Number(found.accountId) > 0) {
    return `account:${Number(found.accountId)}`;
  }
  if (Number.isInteger(found.assigneeId) && Number(found.assigneeId) > 0) {
    return `assignee:${Number(found.assigneeId)}`;
  }
  return '';
};

export const applyRoleAssignmentSelection = (
  assignments: unknown,
  roleId: number,
  selectedValue: string,
  optionsByRoleId: RoleAssignmentOptionsByRoleId
): RoleAssignment[] => {
  const normalizedAssignments = normalizeRoleAssignments(assignments);
  const nextAssignments = normalizedAssignments.filter(
    (assignment) => assignment.roleId !== roleId
  );

  if (!selectedValue) {
    return nextAssignments;
  }

  const options = optionsByRoleId[roleId] || [];
  const selectedOption = options.find((option) => option.key === selectedValue);
  if (!selectedOption) {
    return nextAssignments;
  }

  nextAssignments.push({
    roleId,
    accountId:
      Number.isInteger(selectedOption.accountId) && Number(selectedOption.accountId) > 0
        ? Number(selectedOption.accountId)
        : null,
    assigneeId:
      Number.isInteger(selectedOption.assigneeId) && Number(selectedOption.assigneeId) > 0
        ? Number(selectedOption.assigneeId)
        : null,
  });

  return nextAssignments.sort((first, second) => first.roleId - second.roleId);
};

export const buildRoleAssignmentFilterOptions = (
  roles: RoleAssignmentRole[],
  optionsByRoleId: RoleAssignmentOptionsByRoleId
): Array<{ value: string; label: string }> => {
  const result: Array<{ value: string; label: string }> = [];
  const seen = new Set<string>();

  for (const role of roles) {
    const roleOptions = optionsByRoleId[role.id] || [];
    for (const option of roleOptions) {
      const key = String(option.key || '').trim();
      const label = String(option.label || '').trim();
      if (!key || !label || seen.has(key)) {
        continue;
      }
      seen.add(key);
      result.push({ value: key, label });
    }
  }

  return result.sort((first, second) => first.label.localeCompare(second.label));
};

export const buildRoleAssignmentManageEntries = (
  teamMembers: RoleAssignmentManageTeamMember[],
  assignees: RoleAssignmentManageAssignee[]
): RoleAssignmentManageEntry[] => {
  const result: RoleAssignmentManageEntry[] = [];
  const usedKeys = new Set<string>();

  for (const teamMember of Array.isArray(teamMembers) ? teamMembers : []) {
    const accountId = Number(teamMember?.accountId);
    const userId = String(teamMember?.userId || '').trim();
    const key =
      Number.isInteger(accountId) && accountId > 0
        ? `account:${accountId}`
        : `team_member:${userId}`;
    if ((!Number.isInteger(accountId) || accountId <= 0) && !userId) {
      continue;
    }
    if (!key || usedKeys.has(key)) {
      continue;
    }

    const firstName = String(teamMember?.firstName || '').trim();
    const lastName = String(teamMember?.lastName || '').trim();
    const email = String(teamMember?.email || '').trim();
    const name = `${firstName} ${lastName}`.trim() || email || 'Team Member';

    result.push({
      key,
      type: 'team_member',
      name,
      label: normalizeLabel(teamMember?.label),
      roleIds: normalizeRoleIds(teamMember?.roles),
      teamMember,
    });
    usedKeys.add(key);
  }

  for (const assignee of Array.isArray(assignees) ? assignees : []) {
    const assigneeId = Number(assignee?.id);
    const key = Number.isInteger(assigneeId) && assigneeId > 0 ? `assignee:${assigneeId}` : '';
    if (!key || usedKeys.has(key)) {
      continue;
    }

    const name = String(assignee?.name || '').trim() || 'Assignee';
    result.push({
      key,
      type: 'assignee',
      name,
      label: normalizeLabel(assignee?.label),
      roleIds: normalizeRoleIds(assignee?.roles),
      assignee,
    });
    usedKeys.add(key);
  }

  return result.sort((first, second) => {
    const nameComparison = first.name.localeCompare(second.name, undefined, {
      sensitivity: 'base',
    });
    if (nameComparison !== 0) {
      return nameComparison;
    }

    const labelComparison = first.label.localeCompare(second.label, undefined, {
      sensitivity: 'base',
    });
    if (labelComparison !== 0) {
      return labelComparison;
    }

    return first.type.localeCompare(second.type);
  });
};

export const countRoleAssignmentManageEntriesForRole = (
  entries: RoleAssignmentManageEntry[],
  roleId: number
): number => {
  return entries.filter((entry) => entry.roleIds.includes(roleId)).length;
};

export const getAssignmentOwnerKeys = (assignments: unknown): string[] => {
  const normalizedAssignments = normalizeRoleAssignments(assignments);
  const keys: string[] = [];
  const seen = new Set<string>();

  for (const assignment of normalizedAssignments) {
    if (Number.isInteger(assignment.accountId) && Number(assignment.accountId) > 0) {
      const key = `account:${Number(assignment.accountId)}`;
      if (!seen.has(key)) {
        seen.add(key);
        keys.push(key);
      }
    }
    if (Number.isInteger(assignment.assigneeId) && Number(assignment.assigneeId) > 0) {
      const key = `assignee:${Number(assignment.assigneeId)}`;
      if (!seen.has(key)) {
        seen.add(key);
        keys.push(key);
      }
    }
  }

  return keys;
};
